import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormField, FormFieldLabel, FormFieldValue } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { toDisplayDate, toStorageDate, todayStorage, isDisabledDate } from 'utils/datePickerUtils';
import DateCalendarPopover from './DateCalendarPopover';

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

const DateSeparator = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 14px;
`;

const InputWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${(p) => p.$width || '120px'};
`;

const CalBtn = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 14px;
  cursor: pointer;
  z-index: 1;
  &:hover:not(:disabled) {
    color: ${theme.colors.primary};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DateInput = styled(FormFieldValue)`
  padding-right: 26px;
`;

/**
 * DateRangeField (M1 기간)
 * - From~To 직접 입력 + 공통 캘린더(클릭 2번·프리셋·적용/닫기)
 * - 저장값: 각 필드 YYYYMMDD
 */
const DateRangeField = ({
  name,
  label,
  startFieldName,
  endFieldName,
  startValue = '',
  endValue = '',
  width,
  labelWidth,
  fieldWidth,
  showColon = false,
  readonly = false,
  min,
  max,
  showTodayShortcut = true,
  showPresets = true,
  presetSet = 'inquiry',
  fiscalYear,
  anchorFrom,
  yearPreset = 'calendar',
  onChange,
  fieldProps = {},
}) => {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [startText, setStartText] = useState(toDisplayDate(startValue));
  const [endText, setEndText] = useState(toDisplayDate(endValue));

  useEffect(() => {
    setStartText(toDisplayDate(startValue));
  }, [startValue]);
  useEffect(() => {
    setEndText(toDisplayDate(endValue));
  }, [endValue]);

  const emit = (field, storage) => {
    if (!onChange) return;
    onChange({ target: { name: field, value: storage || '' } });
  };

  const openPicker = () => {
    if (readonly) return;
    const rect = wrapRef.current?.getBoundingClientRect?.();
    if (rect) {
      setAnchor({ top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right });
    }
    setOpen(true);
  };

  const commitSide = (side, raw) => {
    const field = side === 'start' ? startFieldName : endFieldName;
    const trimmed = String(raw || '').trim();
    if (!trimmed) {
      emit(field, '');
      if (side === 'start') setStartText('');
      else setEndText('');
      return;
    }
    const stor = toStorageDate(trimmed);
    if (!stor || isDisabledDate(stor, min, max)) {
      if (side === 'start') setStartText(toDisplayDate(startValue));
      else setEndText(toDisplayDate(endValue));
      return;
    }
    emit(field, stor);
    if (side === 'start') setStartText(toDisplayDate(stor));
    else setEndText(toDisplayDate(stor));
  };

  const inputW = width || '120px';

  return (
    <FormField width={fieldWidth || 'auto'}>
      {label && (
        <FormFieldLabel width={labelWidth}>{showColon ? `${label}:` : label}</FormFieldLabel>
      )}
      <DateRangeContainer ref={wrapRef}>
        <InputWrap $width={inputW}>
          <DateInput
            type="text"
            name={startFieldName}
            inputMode="numeric"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            title="시작일"
            value={startText}
            readOnly={readonly}
            disabled={readonly}
            onChange={readonly ? undefined : (e) => setStartText(e.target.value)}
            onBlur={readonly ? undefined : () => commitSide('start', startText)}
            width="100%"
            {...fieldProps}
          />
          <CalBtn
            type="button"
            tabIndex={-1}
            disabled={readonly}
            title="기간 캘린더"
            aria-label="기간 캘린더 열기"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openPicker}
          >
            ▤
          </CalBtn>
        </InputWrap>
        <DateSeparator>~</DateSeparator>
        <InputWrap $width={inputW}>
          <DateInput
            type="text"
            name={endFieldName}
            inputMode="numeric"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            title="종료일"
            value={endText}
            readOnly={readonly}
            disabled={readonly}
            onChange={readonly ? undefined : (e) => setEndText(e.target.value)}
            onBlur={readonly ? undefined : () => commitSide('end', endText)}
            width="100%"
            {...fieldProps}
          />
          <CalBtn
            type="button"
            tabIndex={-1}
            disabled={readonly}
            title="기간 캘린더"
            aria-label="기간 캘린더 열기"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openPicker}
          >
            ▤
          </CalBtn>
        </InputWrap>
      </DateRangeContainer>
      {open && (
        <DateCalendarPopover
          mode="range"
          startValue={toStorageDate(startValue) || todayStorage()}
          endValue={toStorageDate(endValue)}
          min={min}
          max={max}
          showTodayShortcut={showTodayShortcut}
          showPresets={showPresets}
          presetSet={presetSet}
          fiscalYear={fiscalYear}
          anchorFrom={anchorFrom}
          yearPreset={yearPreset}
          anchorRect={anchor}
          onClose={() => setOpen(false)}
          onApplyRange={({ from, to }) => {
            emit(startFieldName, from);
            emit(endFieldName, to);
            setStartText(toDisplayDate(from));
            setEndText(toDisplayDate(to));
            setOpen(false);
          }}
        />
      )}
    </FormField>
  );
};

export default DateRangeField;
